import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { Inbox, Plus } from "lucide-react";

import Loading from "../../components/Loading";
import { WFPButton } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { FilterBar, type FilterDefinition } from "@/components/FilterBar";
import { getPaginationItems } from "@/lib/pagination";
import { displayErrorMessage } from "../../utils";
import { FILTER_ALL_VALUE, isFilterSet } from "../../utils/filters";
import { getLocalisedName, orderByLocalName } from "../../utils/i18n";
import {
  listBidRequests,
  listBidRequestsReset,
  listCommodities,
  listCommoditiesReset,
} from "../../actions";
import { BidRequestCard } from "./components/BidRequestCard";
import { BID_REQUEST_STATUS, type BidRequest, type BidRequestStatus } from "./types";
import { getStatusLabel } from "./utils";

export const BID_REQUESTS_PAGE_SIZE = 10;

const CREATE_PATH = "/representative/bidRequests/new";
const DETAIL_PATH = (id: number, readOnly: boolean) =>
  `/${readOnly ? "wfpManager" : "representative"}/bidRequests/${id}`;

const STATUS_OPTIONS: BidRequestStatus[] = [
  BID_REQUEST_STATUS.openToBids,
  BID_REQUEST_STATUS.biddingComplete,
  BID_REQUEST_STATUS.acceptedBid,
  BID_REQUEST_STATUS.cancelled,
];

interface CommodityOption {
  id: number;
  name: string;
  name_translations?: Record<string, string>;
}

const COMMODITIES_PAGE_SIZE = 200;

type FilterKey = "status" | "commodity";

const DEFAULT_FILTERS: Record<FilterKey, string> = {
  status: FILTER_ALL_VALUE,
  commodity: FILTER_ALL_VALUE,
};

interface ListParams {
  page: number;
  page_size: number;
  status?: BidRequestStatus;
  commodity?: number;
}

interface StateProps {
  bidRequests: BidRequest[];
  count: number;
  isFetching: boolean;
  errorMessage: string;
  commodities: CommodityOption[];
}

interface DispatchProps {
  listBidRequests: (params: ListParams) => void;
  listBidRequestsReset: () => void;
  listCommodities: (params: { page: number; page_size: number }) => void;
  listCommoditiesReset: () => void;
}

type BidRequestsProps = StateProps & DispatchProps & RouteComponentProps & { readOnly?: boolean };

export const BidRequests = ({
  bidRequests,
  count,
  isFetching,
  errorMessage,
  commodities,
  listBidRequests,
  listBidRequestsReset,
  listCommodities,
  listCommoditiesReset,
  history,
  readOnly = false,
}: BidRequestsProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<FilterKey, string>>(DEFAULT_FILTERS);

  useEffect(() => {
    const params: ListParams = { page, page_size: BID_REQUESTS_PAGE_SIZE };
    if (isFilterSet(filters.status)) {
      params.status = filters.status as BidRequestStatus;
    }
    if (isFilterSet(filters.commodity)) {
      params.commodity = Number(filters.commodity);
    }
    listBidRequests(params);
  }, [page, filters.status, filters.commodity, listBidRequests]);

  useEffect(() => {
    listCommodities({ page: 1, page_size: COMMODITIES_PAGE_SIZE });
    return () => {
      listBidRequestsReset();
      listCommoditiesReset();
    };
  }, [listCommodities, listBidRequestsReset, listCommoditiesReset]);

  const totalPages = Math.max(1, Math.ceil(count / BID_REQUESTS_PAGE_SIZE));
  const goToCreate = () => history.push(CREATE_PATH);

  const hasFilters = isFilterSet(filters.status) || isFilterSet(filters.commodity);
  const isPristineEmpty = !isFetching && bidRequests.length === 0 && !hasFilters;

  const filterDefinitions = useMemo<FilterDefinition<FilterKey>[]>(
    () => [
      {
        key: "status",
        label: t("bidRequests.filters.status"),
        options: STATUS_OPTIONS.map(option => ({
          value: option,
          label: getStatusLabel(option, t),
        })),
      },
      {
        key: "commodity",
        label: t("bidRequests.filters.commodity"),
        searchable: true,
        options: orderByLocalName(commodities).map((commodity: CommodityOption) => ({
          value: String(commodity.id),
          label: String(getLocalisedName(commodity) ?? commodity.name),
        })),
      },
    ],
    [t, commodities]
  );

  const handleFilterChange = (patch: Partial<Record<FilterKey, string>>) => {
    setPage(1);
    setFilters(current => ({ ...current, ...patch }));
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters(DEFAULT_FILTERS);
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-semibold text-2xl">{t("bidRequests.title")}</h1>
        {!readOnly && !isPristineEmpty && (
          <WFPButton onClick={goToCreate}>
            <Plus className="size-4" />
            {t("bidRequests.newBidRequest")}
          </WFPButton>
        )}
      </div>

      {errorMessage && displayErrorMessage(errorMessage)}

      {!isPristineEmpty && (
        <FilterBar
          definitions={filterDefinitions}
          values={filters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />
      )}

      {isFetching ? (
        <Loading isVisible />
      ) : isPristineEmpty ? (
        <div className="flex flex-col items-center gap-4 py-20 text-center">
          <span className="inline-flex size-12 items-center justify-center rounded-lg border border-border text-muted-foreground">
            <Inbox className="size-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className="font-semibold text-lg">{t("bidRequests.empty.title")}</p>
            <p className="max-w-md text-muted-foreground text-sm">
              {t(
                readOnly ? "bidRequests.empty.managerDescription" : "bidRequests.empty.description"
              )}
            </p>
          </div>
          {!readOnly && (
            <WFPButton onClick={goToCreate}>
              <Plus className="size-4" />
              {t("bidRequests.newBidRequest")}
            </WFPButton>
          )}
        </div>
      ) : bidRequests.length === 0 ? (
        <p className="py-16 text-center text-muted-foreground text-sm">
          {t("bidRequests.empty.noMatches")}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {bidRequests.map(bidRequest => (
            <BidRequestCard
              key={bidRequest.id}
              bidRequest={bidRequest}
              onViewDetails={id => history.push(DETAIL_PATH(id, readOnly))}
            />
          ))}

          {totalPages > 1 && (
            <Pagination className="pt-2">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => page > 1 && setPage(page - 1)}
                    disabled={page <= 1}
                  />
                </PaginationItem>
                {getPaginationItems(page, totalPages).map((item, index) =>
                  item === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink isActive={item === page} onClick={() => setPage(item)}>
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => page < totalPages && setPage(page + 1)}
                    disabled={page >= totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
    </main>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  errorMessage: string;
  data: T;
}

interface RootState {
  listBidRequests: RequestState<{ results?: BidRequest[]; count?: number }>;
  listCommodities: RequestState<{ results?: CommodityOption[] }>;
}

export const mapStateToProps = (state: RootState): StateProps => ({
  bidRequests: state.listBidRequests.data.results ?? [],
  count: state.listBidRequests.data.count ?? 0,
  isFetching: state.listBidRequests.isFetching,
  errorMessage: state.listBidRequests.errorMessage,
  commodities: state.listCommodities.data.results ?? [],
});

type AppDispatch = Dispatch<UnknownAction>;

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listBidRequests: params => dispatch(listBidRequests(params) as unknown as UnknownAction),
  listBidRequestsReset: () => dispatch(listBidRequestsReset() as unknown as UnknownAction),
  listCommodities: params => dispatch(listCommodities(params) as unknown as UnknownAction),
  listCommoditiesReset: () => dispatch(listCommoditiesReset() as unknown as UnknownAction),
});

export const ConnectedBidRequests = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(BidRequests));

export const WfpManagerBidRequests = () => <ConnectedBidRequests readOnly />;
